import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, RefreshCw, Save, Webhook, Workflow } from 'lucide-react';

const ADDRESS_ENRICHMENT_ID = 'contract-address-enrichment';
const AUTOMATIONS_ENDPOINT = '/contracts/settings?resource=automations';

function toFieldListText(fields = []) {
  return Array.isArray(fields) ? fields.join(', ') : String(fields || '');
}

function parseFieldList(text) {
  return String(text || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function rowsToText(rows = [], valueKey) {
  const readableRows = (Array.isArray(rows) ? rows : []).map((row) => {
    const next = {
      municipality: row.municipality || '',
      postcode: row.postcode || '',
      county: row.county || '',
      state: row.state || '',
      [valueKey]: row.value || row[valueKey] || '',
    };
    Object.keys(next).forEach((key) => {
      if (!next[key]) delete next[key];
    });
    return next;
  });
  return JSON.stringify(readableRows, null, 2);
}

function parseRowsText(text, valueKey) {
  if (!String(text || '').trim()) return [];
  const parsed = JSON.parse(text);
  if (!Array.isArray(parsed)) {
    throw new Error('Zuordnungen muessen ein JSON-Array sein.');
  }
  return parsed.map((row) => ({
    municipality: String(row?.municipality || '').trim(),
    postcode: String(row?.postcode || '').trim(),
    county: String(row?.county || '').trim(),
    state: String(row?.state || '').trim(),
    value: String(row?.value || row?.[valueKey] || '').trim(),
  }));
}

function StatusPill({ enabled }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid', borderColor: enabled ? '#A7F3D0' : '#FED7AA', background: enabled ? '#ECFDF5' : '#FFF7ED', color: enabled ? '#047857' : '#9A3412', borderRadius: 999, padding: '4px 8px', fontSize: 11, fontWeight: 700 }}>
      {enabled ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
      {enabled ? 'Aktiv' : 'Pausiert'}
    </span>
  );
}

export default function VertragsAutomationsPage({ apiBase = '/api', session = null }) {
  const accessToken = session?.access_token || null;
  const authHeaders = useMemo(() => (
    accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
  ), [accessToken]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [draft, setDraft] = useState(null);
  // An/Aus nur fuer Admins (seit 02.10.2026, der Server lehnt es sonst ab).
  const [istAdmin, setIstAdmin] = useState(false);
  const [triggerStagesText, setTriggerStagesText] = useState('');
  const [sourceFieldsText, setSourceFieldsText] = useState('');
  const [courtMappingsText, setCourtMappingsText] = useState('[]');
  const [gridMappingsText, setGridMappingsText] = useState('[]');

  const automation = draft?.automations?.[ADDRESS_ENRICHMENT_ID] || null;

  const loadAutomations = useCallback(async () => {
    if (!accessToken) {
      setSettings(null);
      setDraft(null);
      setLogs([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${apiBase}${AUTOMATIONS_ENDPOINT}`, { headers: authHeaders });
      const loadedSettings = response.data?.settings || null;
      const loadedAutomation = loadedSettings?.automations?.[ADDRESS_ENRICHMENT_ID] || null;
      setSettings(loadedSettings);
      setDraft(loadedSettings);
      setIstAdmin(response.data?.isAdmin === true);
      setLogs(Array.isArray(response.data?.logs) ? response.data.logs : []);
      setWebhookUrl(response.data?.webhook?.addressEnrichment?.url || '');
      setTriggerStagesText(toFieldListText(loadedAutomation?.triggerStageIds || [loadedAutomation?.triggerStageId].filter(Boolean)));
      setSourceFieldsText(toFieldListText(loadedAutomation?.sourceFields || []));
      setCourtMappingsText(rowsToText(loadedAutomation?.lookups?.courtMappings || [], 'court'));
      setGridMappingsText(rowsToText(loadedAutomation?.lookups?.gridOperatorMappings || [], 'gridOperator'));
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Automatisierungen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [accessToken, apiBase, authHeaders]);

  useEffect(() => {
    loadAutomations();
  }, [loadAutomations]);

  const updateAutomation = useCallback((patch) => {
    setDraft((current) => {
      const source = current || { automations: {} };
      const currentAutomation = source.automations?.[ADDRESS_ENRICHMENT_ID] || {};
      return {
        ...source,
        automations: {
          ...source.automations,
          [ADDRESS_ENRICHMENT_ID]: {
            ...currentAutomation,
            ...patch,
          },
        },
      };
    });
  }, []);

  const updateNestedAutomation = useCallback((section, patch) => {
    setDraft((current) => {
      const source = current || { automations: {} };
      const currentAutomation = source.automations?.[ADDRESS_ENRICHMENT_ID] || {};
      return {
        ...source,
        automations: {
          ...source.automations,
          [ADDRESS_ENRICHMENT_ID]: {
            ...currentAutomation,
            [section]: {
              ...(currentAutomation[section] || {}),
              ...patch,
            },
          },
        },
      };
    });
  }, []);

  const saveAutomations = useCallback(async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const courtMappings = parseRowsText(courtMappingsText, 'court');
      const gridOperatorMappings = parseRowsText(gridMappingsText, 'gridOperator');
      const triggerStageIds = parseFieldList(triggerStagesText);
      const nextAutomation = {
        ...automation,
        triggerStageId: triggerStageIds[0] || automation?.triggerStageId || '',
        triggerStageIds,
        sourceFields: parseFieldList(sourceFieldsText),
        lookups: {
          ...(automation?.lookups || {}),
          courtMappings,
          gridOperatorMappings,
        },
      };
      const payload = {
        automations: {
          ...(draft.automations || {}),
          [ADDRESS_ENRICHMENT_ID]: nextAutomation,
        },
      };
      const response = await axios.post(`${apiBase}${AUTOMATIONS_ENDPOINT}`, payload, { headers: authHeaders });
      const savedSettings = response.data?.settings || payload;
      setSettings(savedSettings);
      setDraft(savedSettings);
      toast.success('Automatisierungen gespeichert.');
      loadAutomations();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }, [apiBase, authHeaders, automation, courtMappingsText, draft, gridMappingsText, loadAutomations, sourceFieldsText, triggerStagesText]);

  return (
    <div style={{ flex: 1, minHeight: 0, background: '#F8FAFC', overflowY: 'auto' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0', background: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 12 }}>
        <Workflow size={20} color="#122A5C" />
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, color: '#122A5C', fontFamily: 'Outfit, sans-serif', fontSize: 18, fontWeight: 700, letterSpacing: '-0.03em' }}>Automatisierungen</h2>
          <div style={{ color: '#64748B', fontSize: 12 }}>Konfigurierte Bitrix-Abläufe, Trigger und letzte Ausführungen.</div>
        </div>
        <button
          onClick={loadAutomations}
          disabled={loading}
          style={{ padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: 8, background: '#FFFFFF', color: '#0F172A', cursor: loading ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}
        >
          <RefreshCw size={13} />
          Aktualisieren
        </button>
        <button
          onClick={saveAutomations}
          disabled={saving || !automation}
          style={{ padding: '8px 11px', border: '1px solid #122A5C', borderRadius: 8, background: saving ? '#CBD5E1' : '#122A5C', color: '#FFFFFF', cursor: saving || !automation ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}
        >
          <Save size={13} />
          {saving ? 'Speichert...' : 'Speichern'}
        </button>
      </div>

      <div style={{ padding: 20, display: 'grid', gap: 14 }}>
        {!automation ? (
          <div style={{ color: '#64748B', fontSize: 13 }}>Lade Automatisierungen...</div>
        ) : (
          <>
            <section style={{ border: '1px solid #E2E8F0', borderRadius: 8, background: '#FFFFFF' }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid #E2E8F0', display: 'flex', gap: 12, alignItems: 'center' }}>
                <StatusPill enabled={automation.enabled} />
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#122A5C', fontSize: 14, fontWeight: 700 }}>{automation.title}</div>
                  <div style={{ color: '#64748B', fontSize: 12 }}>Startet, wenn ein Bitrix-Deal in die Phase „Vertrag erstellen“ geschoben wird.</div>
                </div>
                <label
                  title={istAdmin ? '' : 'An- und Ausschalten dürfen nur Administratoren (auch in der Admin-Übersicht).'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#475569', fontSize: 12, fontWeight: 700, opacity: istAdmin ? 1 : 0.6 }}
                >
                  <input type="checkbox" checked={automation.enabled} disabled={!istAdmin} onChange={(event) => updateAutomation({ enabled: event.target.checked })} />
                  Aktiv{istAdmin ? '' : ' (nur Admins)'}
                </label>
              </div>

              <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                <label style={{ display: 'grid', gap: 5, color: '#475569', fontSize: 12, fontWeight: 700 }}>
                  Trigger-Phasen
                  <input
                    value={triggerStagesText}
                    onChange={(event) => setTriggerStagesText(event.target.value)}
                    style={{ border: '1px solid #CBD5E1', borderRadius: 8, padding: '8px 10px', fontFamily: 'monospace', fontSize: 12 }}
                  />
                  <span style={{ color: '#64748B', fontSize: 11, fontWeight: 500 }}>Kommagetrennt, z.B. C16:UC_S1XD1O, C16:UC_AU8XNN</span>
                </label>
                <label style={{ display: 'grid', gap: 5, color: '#475569', fontSize: 12, fontWeight: 700 }}>
                  Projektadress-Felder
                  <input
                    value={sourceFieldsText}
                    onChange={(event) => setSourceFieldsText(event.target.value)}
                    style={{ border: '1px solid #CBD5E1', borderRadius: 8, padding: '8px 10px', fontFamily: 'monospace', fontSize: 12 }}
                  />
                </label>
                <p style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748B', fontSize: 12, fontWeight: 600, paddingTop: 20, margin: 0 }}>
                  Bereits gefüllte Felder werden nie überschrieben.
                </p>
              </div>

              <div style={{ padding: '0 16px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <label style={{ display: 'grid', gap: 5, color: '#475569', fontSize: 12, fontWeight: 700 }}>
                  Zielfeld Gemeinde
                  <input
                    value={automation.targetFields?.municipality || ''}
                    onChange={(event) => updateNestedAutomation('targetFields', { municipality: event.target.value })}
                    placeholder="UF_CRM_..."
                    style={{ border: '1px solid #CBD5E1', borderRadius: 8, padding: '8px 10px', fontFamily: 'monospace', fontSize: 12 }}
                  />
                </label>
                <label style={{ display: 'grid', gap: 5, color: '#475569', fontSize: 12, fontWeight: 700 }}>
                  Zielfeld Amtsgericht
                  <input
                    value={automation.targetFields?.districtCourt || ''}
                    onChange={(event) => updateNestedAutomation('targetFields', { districtCourt: event.target.value })}
                    placeholder="UF_CRM_..."
                    style={{ border: '1px solid #CBD5E1', borderRadius: 8, padding: '8px 10px', fontFamily: 'monospace', fontSize: 12 }}
                  />
                </label>
                <label style={{ display: 'grid', gap: 5, color: '#475569', fontSize: 12, fontWeight: 700 }}>
                  Zielfeld Netzbetreiber
                  <input
                    value={automation.targetFields?.gridOperator || ''}
                    onChange={(event) => updateNestedAutomation('targetFields', { gridOperator: event.target.value })}
                    placeholder="UF_CRM_..."
                    style={{ border: '1px solid #CBD5E1', borderRadius: 8, padding: '8px 10px', fontFamily: 'monospace', fontSize: 12 }}
                  />
                </label>
              </div>

              <div style={{ padding: '12px 16px', borderTop: '1px solid #E2E8F0', background: '#F8FAFC', display: 'grid', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569', fontSize: 12, fontWeight: 700 }}>
                  <Webhook size={14} />
                  Bitrix Webhook
                </div>
                <code style={{ display: 'block', padding: '9px 10px', border: '1px solid #E2E8F0', borderRadius: 8, background: '#FFFFFF', color: '#0F172A', fontSize: 11, overflowWrap: 'anywhere' }}>{webhookUrl || '-'}</code>
              </div>
            </section>

            <section style={{ border: '1px solid #E2E8F0', borderRadius: 8, background: '#FFFFFF', padding: 16, display: 'grid', gap: 12 }}>
              <div>
                <div style={{ color: '#122A5C', fontSize: 14, fontWeight: 700 }}>Lookup-Quellen</div>
                <div style={{ color: '#64748B', fontSize: 12, marginTop: 2 }}>Gemeinde wird per Lookup normalisiert. Amtsgericht und Netzbetreiber können über gepflegte Zuordnungen oder externe JSON-Endpunkte ergänzt werden.</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
                <label style={{ display: 'grid', gap: 5, color: '#475569', fontSize: 12, fontWeight: 700 }}>
                  Gemeinde Endpoint
                  <input
                    value={automation.lookups?.municipalityLookupUrl || ''}
                    onChange={(event) => updateNestedAutomation('lookups', { municipalityLookupUrl: event.target.value })}
                    placeholder="https://... ?address=..."
                    style={{ border: '1px solid #CBD5E1', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}
                  />
                </label>
                <label style={{ display: 'grid', gap: 5, color: '#475569', fontSize: 12, fontWeight: 700 }}>
                  Amtsgericht Endpoint
                  <input
                    value={automation.lookups?.courtLookupUrl || ''}
                    onChange={(event) => updateNestedAutomation('lookups', { courtLookupUrl: event.target.value })}
                    placeholder="https://... ?municipality=..."
                    style={{ border: '1px solid #CBD5E1', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}
                  />
                </label>
                <label style={{ display: 'grid', gap: 5, color: '#475569', fontSize: 12, fontWeight: 700 }}>
                  Netzbetreiber Endpoint
                  <input
                    value={automation.lookups?.gridOperatorLookupUrl || ''}
                    onChange={(event) => updateNestedAutomation('lookups', { gridOperatorLookupUrl: event.target.value })}
                    placeholder="https://... ?postcode=..."
                    style={{ border: '1px solid #CBD5E1', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}
                  />
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
                <label style={{ display: 'grid', gap: 5, color: '#475569', fontSize: 12, fontWeight: 700 }}>
                  Amtsgericht-Zuordnungen
                  <textarea
                    value={courtMappingsText}
                    onChange={(event) => setCourtMappingsText(event.target.value)}
                    rows={8}
                    style={{ border: '1px solid #CBD5E1', borderRadius: 8, padding: 10, fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }}
                  />
                </label>
                <label style={{ display: 'grid', gap: 5, color: '#475569', fontSize: 12, fontWeight: 700 }}>
                  Netzbetreiber-Zuordnungen
                  <textarea
                    value={gridMappingsText}
                    onChange={(event) => setGridMappingsText(event.target.value)}
                    rows={8}
                    style={{ border: '1px solid #CBD5E1', borderRadius: 8, padding: 10, fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }}
                  />
                </label>
              </div>
            </section>

            <section style={{ border: '1px solid #E2E8F0', borderRadius: 8, background: '#FFFFFF' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #E2E8F0', color: '#122A5C', fontSize: 14, fontWeight: 700 }}>Letzte Läufe</div>
              {logs.length === 0 ? (
                <div style={{ padding: 16, color: '#64748B', fontSize: 12 }}>Noch keine Läufe protokolliert.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                    <thead>
                      <tr style={{ background: '#F8FAFC' }}>
                        <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>Zeit</th>
                        <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>Status</th>
                        <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>Deal</th>
                        <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#475569', borderBottom: '1px solid #E2E8F0' }}>Meldung</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id}>
                          <td style={{ padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #F1F5F9', color: '#475569' }}>{log.createdAt ? new Date(log.createdAt).toLocaleString('de-DE') : '-'}</td>
                          <td style={{ padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #F1F5F9', color: log.status === 'success' ? '#047857' : log.status === 'error' ? '#B91C1C' : '#92400E', fontWeight: 700 }}>{log.status}</td>
                          <td style={{ padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #F1F5F9', color: '#0F172A' }}>{log.dealId ? `#${log.dealId}` : '-'}{log.dealTitle ? ` · ${log.dealTitle}` : ''}</td>
                          <td style={{ padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #F1F5F9', color: '#0F172A' }}>{log.message || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}

        {settings?.updatedAt ? (
          <div style={{ color: '#64748B', fontSize: 11 }}>Zuletzt gespeichert: {new Date(settings.updatedAt).toLocaleString('de-DE')}{settings.updatedBy ? ` · ${settings.updatedBy}` : ''}</div>
        ) : null}
      </div>
    </div>
  );
}
